import { Grid, Button, Box, Typography } from '@mui/material';
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FormProvider, RHFFileField, RHFTextField } from "../../../common/hook-form"
import { apiConstants, postMultipart } from '../../../common/constants';
import { useSnackbar } from 'notistack';
import PageTitle from '../../../common/PageTitle';
import { ManifestCourierFilter } from '../../../common/hook-form/CourierFilter';
import { LoadingButton } from '@mui/lab';
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadSampleFile from '../../../common/DownloadSampleFile';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import MUIModal from '../../../common/MUIModal';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUBTLE, ERROR } from '../../../styles/foundation/colors';
import {
    uploadSectionSx, primaryBtnSx, secondaryBtnSx, helpTextSx, helpTextTypeSx, btnRowSx,
} from '../../../styles/patterns';

function BulkManifest() {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [invalidAwb, setInvalidAwb] = useState([]);
    const { enqueueSnackbar } = useSnackbar()
    const [open, setOpen] = useState(false)
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const validateFileType = (file, fileTypes) => {
        if (!file) return true;
        const validTypes = Array.isArray(fileTypes) ? fileTypes : [fileTypes];
        return validTypes.some((type) => file.type.includes(type));
    };

    Yup.addMethod(Yup.mixed, 'fileType', function (fileTypes) {
        return this.test('fileType', `Invalid file type. Allowed: ${fileTypes.join(', ')}`, function (value) {
            const { path, createError } = this;
            if (value && value.length > 0 && !validateFileType(value[0], fileTypes)) {
                return createError({ path, message: `Invalid file type. Allowed: ${fileTypes.join(', ')}` });
            }
            return true;
        });
    });

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            courier: Yup.mixed().required("Please Select Courier"),
            pickup_pincode: Yup.string().required('Pincode is required').matches(/^\d{6}$/, 'Please enter a valid pincode'),
            file: Yup.mixed().required("Please Select a File").fileType(['text/csv']),
        })),
        defaultValues: { courier: null, pickup_pincode: '', file: null }
    });
    const { handleSubmit, reset } = methods;

    const awbMap = new Map();
    const addAwbToMap = (awbList, reason) => {
        (awbList || []).forEach(awb => {
            if (awbMap.has(awb)) {
                awbMap.set(awb, { ...awbMap.get(awb), reason: awbMap.get(awb).reason + `, ${reason}` });
            } else {
                awbMap.set(awb, { id: awb, awbNumber: awb, reason });
            }
        });
    };

    const onSubmitBulkUpload = async (data) => {
        setIsUploadingFile(true);
        try {
            let form = new FormData();
            form.append("file", data.file[0])
            await postMultipart(`${apiConstants.GENERATE_BULK_MANIFEST}?courierId=${data.courier}&pickupPincode=${data.pickup_pincode}&merchantOid=${merchantId}`, form).then((res) => {
                if (res.status === 200 && res.data?.awbWithErrors === true) {
                    enqueueSnackbar("File Upload Failed", { variant: "error" });
                    setIsUploadingFile(false)
                    setOpen(true)
                    const { invalidAwbList, awbWithDifferentCourier, awbWithDifferentPickup, alreadyManifestedAwbList, awbWithDifferentOrderStatus, duplicateAwbList } = res.data;
                    addAwbToMap(invalidAwbList, "Invalid AWB");
                    addAwbToMap(awbWithDifferentCourier, "Different Courier");
                    addAwbToMap(awbWithDifferentPickup, "Different Pickup");
                    addAwbToMap(alreadyManifestedAwbList, "Already Manifested");
                    addAwbToMap(awbWithDifferentOrderStatus, "Different Order Status");
                    addAwbToMap(duplicateAwbList, "Duplicate AWB");
                    setInvalidAwb(Array.from(awbMap.values()));
                    setIsUploadingFile(false)
                } else if (res.status === 200 && res.data?.awbWithErrors === false) {
                    enqueueSnackbar("Manifest generated successfully", { variant: "success" });
                }
            })
        } catch (err) {
            console.log(err);
            const message = err?.response?.data?.message || err.message;
            enqueueSnackbar(message || "Error uploading file", { variant: "error" });
            setIsUploadingFile(false)
        }
        reset(); setIsUploadingFile(false);
    };

    const columns = [
        { field: 'awbNumber', headerAlign: "left", hideSortIcons: true, headerName: "AWB Number", flex: 1 },
        { field: 'reason', headerAlign: "left", hideSortIcons: true, headerName: "Failure Reason", flex: 2 },
    ]

    return (
        <>


            {/* Error modal */}
            <MUIModal open={open} handleClose={() => setOpen(false)} maxWidth={'md'}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 40, color: ERROR, mb: 1 }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.bold, color: NAVY }}>Upload Failed</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: ERROR, mb: 2 }}>Check the AWB numbers below for details.</Typography>
                </Box>
                <ProzoDataGrid
                    columns={columns} hideFooterPagination={false} autoHeight={true}
                    rows={invalidAwb || []} sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
                    setPage={setPage} page={page} pagination={true}
                    rowCount={invalidAwb?.length || 0} paginationMode={"client"} getRowId={(row) => row.id}
                />
            </MUIModal>

            {/* Upload form */}
            <Box sx={{ ...uploadSectionSx, border: 'none', p: 0 }}>
                <Box sx={helpTextSx}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                    <Typography sx={helpTextTypeSx}>
                        <strong>Instructions:</strong> Select a courier, enter the pickup pincode, and upload a CSV file
                        with AWB numbers (max 1,000 per file). The system will generate a manifest for valid AWBs.
                    </Typography>
                </Box>

                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitBulkUpload)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <ManifestCourierFilter />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="pickup_pincode" label="Pickup Pincode" size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <RHFFileField name="file" fileType={['csv']} label="Upload CSV — maximum 1,000 AWBs per file" />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={btnRowSx}>
                                <LoadingButton startIcon={<FileUploadIcon />} type="submit" loading={isUploadingFile}
                                    variant="contained" size="small" sx={primaryBtnSx}>
                                    Upload
                                </LoadingButton>
                                <Button variant="outlined" size="small" sx={secondaryBtnSx}
                                    startIcon={<RestartAltIcon />} onClick={() => reset()}>
                                    Reset
                                </Button>
                                <DownloadSampleFile fileName={"BULK_MANIFEST"} label='Download Sample' />
                            </Box>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>
        </>
    )
}
export default BulkManifest
